import { useContentProgress } from 'ContentProgress';
import { getStreamProgressStatus } from 'ContentProgress/utils/getProgressStatus';
import { useCurrentUser } from 'FrontRoyalAngular';
import { isIncluded } from 'ProgramInclusion';
import { useMemo } from 'react';
import { useStudentDashboardContent, type StudentDashboardContentResponse } from 'StudentDashboardContent';
import { getProgramInclusion } from 'Users';

function useGetExamFromDashboardContent(dashboardContent: StudentDashboardContentResponse, includedInProgram: boolean) {
    const { streamProgress } = useContentProgress();
    return useMemo(() => {
        // We are only checking if the user has an active exam in their currently active program. This is probably
        // ok, since AI Advisor is only going to have access to the content from their currently active program. So, it
        // wouldn't work to switch to a different program and then try to ask questions about your exam.
        // We only check for active exams when users are included in programs so we don't lock AI Advisor for admins
        // who are testing exams.
        if (!includedInProgram) return null;

        const { lessonStreams: streams = [] } = dashboardContent;
        const exams = streams.filter(s => s.exam);
        const inProgressExam =
            exams.find(
                exam =>
                    getStreamProgressStatus(streamProgress.find(p => p.localePackId === exam.localePackId)) ===
                    'inProgress',
            ) || null;

        return inProgressExam;
    }, [dashboardContent, includedInProgram, streamProgress]);
}

export function useInProgressExam() {
    const user = useCurrentUser();
    const includedInProgram = useMemo(() => isIncluded(getProgramInclusion(user)), [user]);
    const dashboardContent = useStudentDashboardContent();
    const inProgressExam = useGetExamFromDashboardContent(dashboardContent, includedInProgram);

    return { inProgressExam };
}
